import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Plus, Download, Trash2, Syringe, Calendar, Crown } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { enqueueMutation, isOnline } from '@/services/offlineQueue';


export default function Logger() {
  const { user } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    peptide_id: '',
    peptide_name: '',
    dose_amount: '',
    dose_unit: 'mcg',
    injection_site: 'Subcutaneous - Abdomen',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    notes: '',
    side_effects: ''
  });

  const injectionSites = [
    'Subcutaneous - Abdomen',
    'Subcutaneous - Thigh',
    'Subcutaneous - Upper Arm',
    'Intramuscular - Deltoid',
    'Intramuscular - Glute',
    'Intramuscular - Thigh'
  ];

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, peptidesRes] = await Promise.all([
        fetch(`${API}/logs`, { credentials: 'include' }),
        fetch(`${API}/peptides`, { credentials: 'include' })
      ]);

      if (logsRes.ok) setLogs(await logsRes.json());
      if (peptidesRes.ok) setPeptides(await peptidesRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      dose_amount: parseFloat(formData.dose_amount),
    };

    // OFFLINE PATH — queue the mutation and optimistically close the form
    if (!isOnline()) {
      try {
        await enqueueMutation('create_dose_log', payload);
        toast.success('Saved offline — will sync when you reconnect');
        setShowDialog(false);
        setFormData({
          peptide_id: '',
          peptide_name: '',
          dose_amount: '',
          dose_unit: 'mcg',
          injection_site: 'Subcutaneous - Abdomen',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          notes: '',
          side_effects: ''
        });
        // Optimistic UI: append a local entry to logs
        setLogs((prev) => [{ ...payload, log_id: `pending_${Date.now()}`, _pending: true }, ...prev]);
      } catch (err) {
        toast.error('Failed to save offline');
      }
      return;
    }

    try {
      const response = await fetch(`${API}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success('Dose logged successfully');
        setShowDialog(false);
        setFormData({
          peptide_id: '',
          peptide_name: '',
          dose_amount: '',
          dose_unit: 'mcg',
          injection_site: 'Subcutaneous - Abdomen',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          notes: '',
          side_effects: ''
        });
        fetchData();
      } else {
        // If the server rejected for network/5xx reasons, also queue for retry
        if (response.status >= 500) {
          await enqueueMutation('create_dose_log', payload);
          toast.warning('Server busy — saved and will retry');
          setShowDialog(false);
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to log dose');
        }
      }
    } catch (error) {
      // Network error — queue it
      await enqueueMutation('create_dose_log', payload);
      toast.success('Saved offline — will sync when you reconnect');
      setShowDialog(false);
    }
  };

  const handlePeptideSelect = (peptideId) => {
    const peptide = peptides.find(p => p.peptide_id === peptideId);
    setFormData({
      ...formData,
      peptide_id: peptideId,
      peptide_name: peptide?.name || ''
    });
  };

  const exportToPDF = () => {
    if (!isPremium) {
      toast.error('PDF export is a Premium feature');
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(59, 130, 246);
    doc.text('PeptideForge - Dose Log Report', 14, 22);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
    doc.text(`User: ${user?.email || 'N/A'}`, 14, 36);
    
    // Table
    const tableData = logs.map(log => [
      new Date(log.date).toLocaleDateString(),
      log.time || 'N/A',
      log.peptide_name,
      `${log.dose_amount} ${log.dose_unit}`,
      log.injection_site || 'N/A',
      log.side_effects || 'None'
    ]);

    autoTable(doc, {
      startY: 45,
      head: [['Date', 'Time', 'Peptide', 'Dose', 'Site', 'Side Effects']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 8 }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        'FOR RESEARCH USE ONLY - NOT MEDICAL ADVICE',
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    doc.save(`peptideforge_logs_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported successfully');
  };

  const exportToCSV = async () => {
    try {
      const response = await fetch(`${API}/logs/export`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `peptideforge_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">Loading logs...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="logger-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Dose Logger</h1>
          <p className="text-muted-foreground">Log and track your research injections</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-log-button">
                <Plus className="h-4 w-4 mr-2" />
                Log Dose
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Log New Dose</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Peptide</Label>
                  <Select value={formData.peptide_id} onValueChange={handlePeptideSelect}>
                    <SelectTrigger data-testid="peptide-select">
                      <SelectValue placeholder="Select peptide" />
                    </SelectTrigger>
                    <SelectContent>
                      {peptides.map(peptide => (
                        <SelectItem key={peptide.peptide_id} value={peptide.peptide_id}>
                          {peptide.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Dose Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.dose_amount}
                      onChange={(e) => setFormData({...formData, dose_amount: e.target.value})}
                      placeholder="250"
                      required
                      data-testid="dose-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={formData.dose_unit} onValueChange={(v) => setFormData({...formData, dose_unit: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcg">mcg</SelectItem>
                        <SelectItem value="mg">mg</SelectItem>
                        <SelectItem value="IU">IU</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Injection Site</Label>
                  <Select value={formData.injection_site} onValueChange={(v) => setFormData({...formData, injection_site: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {injectionSites.map(site => (
                        <SelectItem key={site} value={site}>{site}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Side Effects (if any)</Label>
                  <Input
                    value={formData.side_effects}
                    onChange={(e) => setFormData({...formData, side_effects: e.target.value})}
                    placeholder="e.g., Mild redness at injection site"
                  />
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional observations..."
                    rows={2}
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="submit-log-button">
                  <Syringe className="h-4 w-4 mr-2" />
                  Log Dose
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>

          <Button 
            variant="outline" 
            onClick={exportToPDF}
            className={!isPremium ? 'opacity-50' : ''}
            data-testid="export-pdf-button"
          >
            <FileText className="h-4 w-4 mr-2" />
            PDF
            {!isPremium && <Crown className="h-3 w-3 ml-1 text-primary" />}
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      {logs.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 glass-card text-center">
            <p className="text-3xl font-bold text-primary">{logs.length}</p>
            <p className="text-sm text-muted-foreground">Total Logs</p>
          </Card>
          <Card className="p-4 glass-card text-center">
            <p className="text-3xl font-bold text-success">
              {new Set(logs.map(l => l.peptide_name)).size}
            </p>
            <p className="text-sm text-muted-foreground">Peptides Used</p>
          </Card>
          <Card className="p-4 glass-card text-center">
            <p className="text-3xl font-bold text-info">
              {logs.filter(l => {
                const logDate = new Date(l.date);
                const today = new Date();
                return logDate.toDateString() === today.toDateString();
              }).length}
            </p>
            <p className="text-sm text-muted-foreground">Today's Doses</p>
          </Card>
          <Card className="p-4 glass-card text-center">
            <p className="text-3xl font-bold text-warning">
              {logs.filter(l => l.side_effects && l.side_effects.trim()).length}
            </p>
            <p className="text-sm text-muted-foreground">With Side Effects</p>
          </Card>
        </div>
      )}

      {/* Logs List */}
      {logs.length === 0 ? (
        <Card className="p-12 text-center glass-card">
          <Syringe className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">No Doses Logged Yet</h2>
          <p className="text-muted-foreground mb-6">
            Start logging your research doses to track your protocols and analyze results.
          </p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Log Your First Dose
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <Card key={log.log_id} className="p-4 glass-card" data-testid={`log-item-${log.log_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Syringe className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{log.peptide_name}</h3>
                    <p className="text-primary font-medium">
                      {log.dose_amount} {log.dose_unit}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(log.date).toLocaleDateString()}
                        {log.time && ` at ${log.time}`}
                      </span>
                      {log.injection_site && (
                        <span className="px-2 py-0.5 rounded bg-secondary">
                          {log.injection_site}
                        </span>
                      )}
                    </div>
                    {log.side_effects && (
                      <p className="text-sm text-warning mt-2">
                        Side effects: {log.side_effects}
                      </p>
                    )}
                    {log.notes && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {log.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
